import React, { FC } from 'react'
import moment from 'moment'
import {
  BarChart as ReBarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  Cell,
  YAxisProps,
} from 'recharts'
import { useTheme } from 'styled-components'
import { IBarChartBarConfigs, IBarChartData } from '../../../types/charts/barChart'
import { convertNumberToCommaSeperated } from '../../../utils'
import RChartContainer from '../RChartContainer'
import RChartTooltip from '../RChartTooltip'
import { CategoricalChartProps } from 'recharts/types/chart/generateCategoricalChart'

interface IBarChartProps extends Omit<CategoricalChartProps, 'children'> {
  data: IBarChartData
  bars: IBarChartBarConfigs
  dataKey: string
  dateXAxis?: boolean
  yAxis?: YAxisProps
}

const RBarChart: FC<IBarChartProps> = ({
  bars = [],
  data = [],
  dateXAxis = false,
  dataKey = '',
  height = 300,
  width = '100%',
  yAxis = {},
  ...reBarChartProps
}) => {
  const theme = useTheme()

  function getDateXAxisLabel(label: string) {
    if (!data.length) return ''

    const MAX_LENGTH_FOR_DATE_STRING = 10
    const DAY_DIFF_FOR_MONTH = 30
    const DAY_DIFF_FOR_YEAR = 360
    const showDateString = data.length < MAX_LENGTH_FOR_DATE_STRING

    const startDate = moment(data[0][dataKey])
    const endDate = moment(data[data.length - 1][dataKey])
    const dayDiff = endDate.diff(startDate, 'days')
    const momentDate = moment(label)

    if (dayDiff > DAY_DIFF_FOR_YEAR) {
      return showDateString ? momentDate.format('YYYY') : momentDate.format('MM/YYYY')
    } else if (dayDiff > DAY_DIFF_FOR_MONTH) {
      return showDateString ? momentDate.format('MMM') : momentDate.format('DD/MM/YYYY')
    }
    return momentDate.format('DD/MM/YYYY')
  }

  return (
    <>
      <RChartContainer height={height} width={width}>
        <ReBarChart data={data} {...reBarChartProps}>
          <CartesianGrid strokeDasharray='3 3' />
          <XAxis
            dataKey={dataKey}
            tickFormatter={(tick) => (dateXAxis ? getDateXAxisLabel(tick) : tick)}
          />
          <YAxis {...yAxis} tickFormatter={convertNumberToCommaSeperated} />
          <Tooltip content={<RChartTooltip />} wrapperStyle={{ outline: 'none' }} />

          {bars.map(({ dataKey, fill }, idx) => (
            <Bar
              key={dataKey}
              dataKey={dataKey}
              fillOpacity={0.9}
              strokeWidth={2}
              radius={[4, 4, 0, 0]}
            >
              {data.map((dataItem) => (
                <Cell
                  key={`cell-${idx}`}
                  fill={
                    typeof fill === 'function'
                      ? theme.getThemedColor(fill(dataItem))
                      : theme.getThemedColor(fill)
                  }
                />
              ))}
            </Bar>
          ))}
        </ReBarChart>
      </RChartContainer>
    </>
  )
}

export default RBarChart
